import * as Highcharts from "highcharts/highstock";
import { calibrateUTCOffset, getMonths } from "@helpers/date";
import { ChartLineColor, LineChartConfig } from "@modules/shared/chart/models/chart-config.model";
import { Derivative } from "@models/derivative/currencies/derivative";
import { Timespan } from "@helpers/const/timespan";
import {
	MarginLimitOrder,
	MarginLimitOrderExitOrderType,
	TimePeriod
} from "../../../../api/graphql/bff/generated/types.graphql-gen";
import { TextLabels } from "@modules/shared/chart/components/line-chart/line-chart.component";

export type TargetPriceFormatter = (order: MarginLimitOrder) => string;

function makePlotLine({
	id,
	value,
	color,
	text,
	type
}: {
	id?: string;
	value: number;
	color: string;
	text?: string;
	type: string;
}): Highcharts.YAxisPlotLinesOptions {
	return {
		id,
		value,
		color,
		dashStyle: "ShortDash",
		width: 1,
		zIndex: 2,
		label: {
			text,
			align: "center",
			y: -8,
			useHTML: true,
			style: {
				color,
				whiteSpace: "nowrap",
				border:
					type === MarginLimitOrderExitOrderType.TakeProfit
						? `1px solid var(--color-positive-stroke-primary)`
						: `1px solid ${color}`
			}
		}
	};
}

function buildLimitOrderPlotLines(
	limitOrders?: MarginLimitOrder[],
	textLabels?: TextLabels,
	formatTargetPrice?: TargetPriceFormatter
): Highcharts.YAxisPlotLinesOptions[] {
	if (!Array.isArray(limitOrders) || limitOrders.length === 0) {
		return [];
	}

	return limitOrders
		.filter((lo) => lo?.targetPrice?.value != null)
		.map((lo, index) => {
			const type = (lo.tradeType ?? "DEFAULT").toString();

			const isTakeProfit = lo.exitOrderType === MarginLimitOrderExitOrderType.TakeProfit;
			const color = isTakeProfit ? "var(--color-positive-text-primary)" : "var(--color-neutrals-stroke-primary)";

			const priceStr = formatTargetPrice ? formatTargetPrice(lo) : String(lo.targetPrice.value);

			const prefix = isTakeProfit ? textLabels?.profit : textLabels?.loss;

			return makePlotLine({
				id: `limit-${type.toLowerCase()}-${index}`,
				value: Number(lo.targetPrice.value),
				color,
				text: `${prefix}${priceStr}`,
				type: lo.exitOrderType
			});
		});
}

export function getLineChartConfig(
	lang: string,
	liquidationPrice?: number,
	textLabels?: TextLabels,
	limitOrders?: MarginLimitOrder[],
	formatTargetPrice?: TargetPriceFormatter
): Highcharts.Options {
	const liqPlotLine: Highcharts.YAxisPlotLinesOptions = liquidationPrice
		? {
				id: "liq-line",
				value: liquidationPrice,
				color: ChartLineColor.Red,
				dashStyle: "ShortDash",
				width: 1,
				zIndex: 3,
				label: {
					text: textLabels.liquidation,
					align: "center",
					y: -8,
					useHTML: true,
					style: {
						color: ChartLineColor.Red,
						whiteSpace: "nowrap",
						border: `1px solid ${ChartLineColor.Red}`
					}
				}
			}
		: null;

	const orderPlotLines = buildLimitOrderPlotLines(limitOrders, textLabels, formatTargetPrice);

	const plotLines: Highcharts.YAxisPlotLinesOptions[] = [...orderPlotLines, ...(liqPlotLine ? [liqPlotLine] : [])];

	return {
		chart: {
			type: "line",
			marginTop: 80,
			animation: false,
			spacing: [0, 0, 0, 6],
			backgroundColor: "transparent",
			events: {
				render: function () {
					setTimeout(() => {
						this.reflow();
					});
				}
			}
		},
		time: {
			timezoneOffset: -calibrateUTCOffset(null).utcOffset()
		},
		lang: {
			months: getMonths(lang),
			shortMonths: getMonths(lang, "short")
		},
		xAxis: [
			{
				type: "datetime",
				lineColor: "transparent",
				minorTickLength: 0, // Hide tick markers
				tickLength: 0, // Hide tick markers
				crosshair: {
					color: "#27d17f",
					dashStyle: "ShortDot"
				},
				offset: 10, // Distance from axis to chart
				labels: {
					align: "center",
					step: 1,
					overflow: "allow",
					style: {
						fontSize: "11px",
						color: "#b4b4b4"
					}
				}
			}
		],
		tooltip: {
			positioner: function (labelWidth, labelHeight, point) {
				return {
					x: point.plotX - labelWidth / 2 + 4, // 4px pad to make it nicely centered
					y: 12
				};
			},
			backgroundColor: "rgba(0,0,0,0)",
			shadow: false,
			borderWidth: 0,
			useHTML: true,
			style: {
				zIndex: 100
			},
			outside: true // allow overflow of tooltip outside of chart
		},
		yAxis: [
			{
				opposite: true,
				gridLineColor: "#d8d8d8",
				gridLineDashStyle: "Dot",
				gridLineWidth: 1,
				labels: {
					style: { fontSize: "11px", color: "#b4b4b4" }
				},
				title: {
					text: null
				},
				plotLines
			}
		],
		legend: {
			enabled: false
		},
		exporting: {
			enabled: false
		},
		title: {
			text: undefined // Disable title
		},
		credits: {
			enabled: false // Disable Highcharts logo
		},
		plotOptions: {
			series: {
				color: ChartLineColor.GreenLegacy,
				states: {
					hover: {
						lineWidthPlus: 0 // Disable line bolding hover effect
					}
				},
				gapSize: 0,
				animation: false
			},
			line: {
				marker: {
					enabled: false
				}
			}
		},
		stockTools: {
			gui: {
				enabled: false
			}
		},
		navigator: {
			enabled: false
		},
		scrollbar: {
			enabled: false
		},
		rangeSelector: {
			enabled: false
		},
		// For reasons unbeknownst to mankind, we have to declare empty series initially
		series: [
			{
				data: [],
				type: "line"
			}
		]
	};
}

export function getLineChartSmallConfig(): Highcharts.Options {
	return {
		chart: {
			type: "line",
			marginTop: 0,
			animation: false,
			spacing: [0, 0, 0, 0],
			backgroundColor: "transparent",
			events: {
				render: function () {
					setTimeout(() => {
						this.reflow();
					});
				}
			}
		},
		time: {
			timezoneOffset: -calibrateUTCOffset(null).utcOffset()
		},
		xAxis: [
			{
				type: "datetime",
				lineColor: "transparent",
				minorTickLength: 0, // Hide tick markers
				tickLength: 0, // Hide tick markers
				crosshair: undefined,
				visible: false
			}
		],
		tooltip: {
			enabled: false
		},
		yAxis: [
			{
				visible: false,
				startOnTick: false,
				endOnTick: false,
				opposite: true,
				title: {
					text: null
				},
				tickPixelInterval: 5 // Scale the axis to fit smaller size
			}
		],
		legend: {
			enabled: false
		},
		exporting: {
			enabled: false
		},
		title: {
			text: undefined // Disable title
		},
		credits: {
			enabled: false // Disable Highcharts logo
		},
		plotOptions: {
			series: {
				color: "#33d778",
				states: {
					hover: {
						enabled: false
					}
				},
				gapSize: 0,
				animation: false
			},
			line: {
				marker: {
					enabled: false
				}
			}
		},
		stockTools: {
			gui: {
				enabled: false
			}
		},
		navigator: {
			enabled: false
		},
		scrollbar: {
			enabled: false
		},
		rangeSelector: {
			enabled: false
		},
		series: [
			{
				data: [],
				type: "line"
			}
		]
	};
}

export function getAreaChartConfig(): Highcharts.Options {
	return {
		chart: {
			type: "areaspline",
			marginTop: 0,
			animation: false,
			spacing: [0, 0, 0, 0],
			backgroundColor: "transparent"
		},
		xAxis: [
			{
				visible: false
			}
		],
		yAxis: [
			{
				min: null,
				max: null,
				visible: false
			}
		],
		legend: {
			enabled: false
		},
		exporting: {
			enabled: false
		},
		title: {
			text: undefined // Disable title
		},
		credits: {
			enabled: false // Disable Highcharts logo
		},
		plotOptions: {
			series: {
				color: ChartLineColor.GreenLegacy,
				states: {
					hover: {
						lineWidthPlus: 0 // Disable line bolding hover effect
					}
				},
				gapSize: 0,
				animation: false
			},
			areaspline: {
				marker: {
					enabled: false
				},
				fillOpacity: 1
			}
		},
		stockTools: {
			gui: {
				enabled: false
			}
		},
		navigator: {
			enabled: false
		},
		scrollbar: {
			enabled: false
		},
		rangeSelector: {
			enabled: false
		},
		// For reasons unbeknownst to mankind, we have to declare empty series initially
		series: [
			{
				data: [],
				type: "areaspline"
			}
		]
	};
}

export function getAdvancedChartConfig(lang: string): Highcharts.Options {
	return {
		chart: {
			type: "candlestick",
			marginTop: 0,
			spacing: [0, 0, 0, 0],
			animation: false
		},
		time: {
			timezoneOffset: -calibrateUTCOffset(null).utcOffset()
		},
		lang: {
			months: getMonths(lang),
			shortMonths: getMonths(lang, "short")
		},
		xAxis: [
			{
				type: "datetime",
				lineColor: "transparent",
				minorTickLength: 0, // Hide tick markers
				tickLength: 0, // Hide tick markers
				crosshair: {
					color: "var(--color-neutral-10)",
					dashStyle: "ShortDot",
					snap: false,
					label: {
						enabled: true,
						style: {
							fontSize: "11px"
						}
					}
				},
				offset: 10, // Distance from axis to chart
				labels: {
					align: "center",
					step: 1,
					overflow: "allow",
					style: {
						fontSize: "11px",
						color: "#b4b4b4"
					}
				}
			}
		],
		stockTools: {
			gui: {
				buttons: [
					"indicators",
					"simpleShapes",
					"lines",
					"crookedLines",
					"measure",
					"advanced",
					"toggleAnnotations",
					"verticalLabels",
					"fullScreen",
					"typeChange"
				],
				definitions: {
					typeChange: {
						items: ["typeOHLC", "typeLine", "typeCandlestick"]
					}
				}
			}
		},
		tooltip: {
			positioner: function (labelWidth, labelHeight, point) {
				return {
					x: point.plotX - labelWidth / 2 + 4, // 4px pad to make it nicely centered
					y: 12
				};
			},
			backgroundColor: "rgba(0,0,0,0)",
			shadow: false,
			borderWidth: 0,
			useHTML: true,
			style: {
				zIndex: 100
			}
		},
		yAxis: [
			{
				opposite: true,
				title: {
					text: null
				},
				height: "95%", // Setting height and top to prevent yAxis from getting cut off
				top: "5%",
				endOnTick: false,
				showLastLabel: true,
				gridLineDashStyle: "Dot",
				labels: {
					align: "center",
					y: 4,
					style: {
						fontSize: "11px"
					}
				},
				resize: {
					enabled: false
				},
				crosshair: {
					color: "var(--color-neutral-10)",
					dashStyle: "ShortDot",
					snap: false,
					label: {
						enabled: true,
						style: {
							fontSize: "11px"
						}
					}
				}
			}
		],
		legend: {
			enabled: false
		},
		exporting: {
			enabled: false
		},
		title: {
			text: undefined // Disable title
		},
		credits: {
			enabled: false // Disable Highcharts logo
		},
		plotOptions: {
			series: {
				color: ChartLineColor.GreenLegacy,
				states: {
					hover: {
						lineWidthPlus: 0 // Disable line bolding hover effect
					}
				},
				gapSize: 0,
				animation: false
			},
			candlestick: {
				color: "#ea4d5c",
				lineColor: "#ea4d5c",
				upColor: "#27d17f",
				upLineColor: "#27d17f"
			}
		},
		navigator: {
			enabled: false
		},
		scrollbar: {
			enabled: false
		},
		rangeSelector: {
			enabled: false
		},
		// For reasons unbeknownst to mankind, we have to declare empty series initially
		series: [
			{
				data: [],
				type: "candlestick"
			}
		]
	};
}

export function getColumnChartConfig(): Highcharts.Options {
	return {
		chart: {
			type: "column",
			spacing: [6, 6, 6, 6]
		},
		legend: {
			enabled: false
		},
		title: {
			text: undefined // Disable title
		},
		credits: {
			enabled: false // Disable Highcharts logo
		},
		yAxis: {
			opposite: true,
			title: {
				text: null
			},
			gridLineColor: "#d8d8d8",
			gridLineWidth: 2,
			tickPixelInterval: 30 // Make ticks a little denser
		},
		tooltip: {
			enabled: false
		},
		plotOptions: {
			series: {
				color: ChartLineColor.GreenLegacy,
				states: {
					hover: {
						lineWidthPlus: 0 // Disable line bolding hover effect
					}
				},
				animation: false,
				borderWidth: 0
			},
			column: {
				pointWidth: 5,
				minPointLength: 2 // fixes unseen candles
			}
		}
	};
}

export function formatTooltipTime(locale: string, timestamp: number, timespan: Timespan | TimePeriod): string {
	const date = new Date(timestamp);

	// @ts-ignore
	if ([Timespan.SixMonths, Timespan.FiveYears, Timespan.Year, Timespan.Max].includes(timespan)) {
		return date.toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric" });
	}

	return date.toLocaleTimeString(locale, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

// Returns the timespan between two candles as a time string
export function getCandleResolution(timespan: Timespan): string {
	switch (timespan) {
		case Timespan.Day:
			return "10M";
		case Timespan.Week:
			return "60M";
		case Timespan.Month:
			return "6H";
		case Timespan.SixMonths:
			return "1D";
		case Timespan.Year:
			return "1D";
		case Timespan.FiveYears:
			return "1D";
		case Timespan.Max:
			return "1D";
		default:
			return "10M";
	}
}

export function unixTimeToLocaleString(locale: string, value: number, timespan: Timespan | TimePeriod): string {
	const date = new Date(value);

	const timespanValues = new Set(Object.values(Timespan));

	let type: "Timespan" | "TimePeriod";

	// @ts-ignore
	if (timespanValues.has(timespan)) {
		type = "Timespan";
	} else {
		type = "TimePeriod";
	}

	if (type === "Timespan") {
		switch (timespan) {
			case Timespan.Day:
				return date.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
			case Timespan.Week:
			case Timespan.Month:
				return date.toLocaleDateString(locale, { month: "numeric", day: "numeric" });
			case Timespan.SixMonths:
			case Timespan.Year:
				return date.toLocaleDateString(locale, { month: "short" });
			case Timespan.FiveYears:
			case Timespan.Max:
				return date.toLocaleDateString(locale, { month: "short", year: "numeric" });
			default:
				return date.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
		}
	} else if (type === "TimePeriod") {
		switch (timespan) {
			case TimePeriod.Day:
				return date.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
			case TimePeriod.Week:
			case TimePeriod.Month:
				return date.toLocaleDateString(locale, { month: "numeric", day: "numeric" });
			case TimePeriod.SixMonths:
			case TimePeriod.Year:
				return date.toLocaleDateString(locale, { month: "short" });
			case TimePeriod.FiveYears:
				return date.toLocaleDateString(locale, { month: "short", year: "numeric" });
			default:
				return date.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
		}
	} else {
		return date.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
	}
}

export function supportsIntraday(config: LineChartConfig): boolean {
	return (
		config.lastTradingDayStart &&
		config.lastTradingDayEnd &&
		config.tradable instanceof Derivative &&
		config.timespan === Timespan.Day
	);
}

export function getChartLineColor(priceChange: number): ChartLineColor {
	if (priceChange > 0) {
		return ChartLineColor.Green;
	} else if (priceChange < 0) {
		return ChartLineColor.Red;
	}

	return ChartLineColor.Gray;
}
